import { useCallback, useEffect, useRef, useState } from 'react';

import { clampPercentage } from '../utils/ambienceSettings.utils';

type SoundPreviewState = Record<string, boolean>;

export const useSoundPreview = () => {
  const previewAudiosRef = useRef<Record<string, HTMLAudioElement>>({});
  const [playingPreviewKeys, setPlayingPreviewKeys] = useState<SoundPreviewState>({});

  const setPreviewPlaying = useCallback((key: string, isPlaying: boolean) => {
    setPlayingPreviewKeys((prev) => {
      if (isPlaying) {
        return { ...prev, [key]: true };
      }

      if (!prev[key]) {
        return prev;
      }

      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const disposePreview = useCallback(
    (key: string, shouldResetState = true) => {
      const audio = previewAudiosRef.current[key];

      if (audio) {
        audio.onended = null;
        audio.onerror = null;
        audio.pause();
        audio.currentTime = 0;
        delete previewAudiosRef.current[key];
      }

      if (shouldResetState) {
        setPreviewPlaying(key, false);
      }
    },
    [setPreviewPlaying],
  );

  const stopPreview = useCallback(
    (key: string) => {
      disposePreview(key);
    },
    [disposePreview],
  );

  const stopAllPreviews = useCallback(
    (exceptKey?: string) => {
      Object.keys(previewAudiosRef.current).forEach((key) => {
        if (key !== exceptKey) {
          disposePreview(key);
        }
      });
    },
    [disposePreview],
  );

  const updatePreviewVolume = useCallback((key: string, volume: number) => {
    const audio = previewAudiosRef.current[key];

    if (!audio) {
      return;
    }

    audio.volume = clampPercentage(volume) / 100;
  }, []);

  const togglePreview = useCallback(
    (key: string, fileUrl?: string, volume = 0) => {
      if (previewAudiosRef.current[key]) {
        stopPreview(key);
        return;
      }

      if (!fileUrl) {
        return;
      }

      stopAllPreviews(key);

      const nextAudio = new Audio(fileUrl);
      nextAudio.volume = clampPercentage(volume) / 100;
      previewAudiosRef.current[key] = nextAudio;

      nextAudio.onended = () => {
        disposePreview(key);
      };

      nextAudio.onerror = () => {
        disposePreview(key);
      };

      setPreviewPlaying(key, true);
      nextAudio.play().catch(() => {
        disposePreview(key);
      });
    },
    [disposePreview, setPreviewPlaying, stopAllPreviews, stopPreview],
  );

  useEffect(() => {
    return () => {
      Object.keys(previewAudiosRef.current).forEach((key) => {
        disposePreview(key, false);
      });

      previewAudiosRef.current = {};
    };
  }, [disposePreview]);

  return {
    playingPreviewKeys,
    stopPreview,
    togglePreview,
    updatePreviewVolume,
  };
};
